import React, { useState } from "react";
import { useAuth } from "../Context/AuthContext";
import "../Styling/ProjectProgressDashboard.css";
import axios from "axios";
import { Base_URL } from "../url";
import { useEffect } from "react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const ProjectProgressDashboard = () => {
  const { user } = useAuth(); // 👈 get auth data
  const orgId = user?.organisationId || "";

  const [filters, setFilters] = useState({
    type: "All",
    client: "",
    project: "",
    resource: "",
    submittedBy: "",
    fromDate: "",
    toDate: "",
    dueDate: "",
    status: "All",
    dueStatus: "All",
    changedFromDate: "",
    changedToDate: "",
  });

  const [dashboardData, setDashboardData] = useState({
    client: [],
    project: [],
    priority: [],
    resource: [],
  });

  const [expanded, setExpanded] = useState({
    client: false,
    project: false,
    priority: false,
    resource: false,
  });

  const [dropdownData, setDropdownData] = useState({
    clients: [],
    projects: [],
    types: [],
    resources: [],
    submittedBy: [],
    statuses: [],
  });

  const handleChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleDownloadExcel = async () => {
    try {
      let dataToUse = dashboardData;

      // ❗ If no data on screen → fetch ALL data
      const isEmpty =
        !dashboardData.client.length &&
        !dashboardData.project.length &&
        !dashboardData.priority.length &&
        !dashboardData.resource.length;

      if (isEmpty) {
        const res = await axios.get(`${Base_URL}/ProjectProgress/list-v2`, {
          params: { orgId },
        });
        console.log(res);

        const apiData = res.data?.data || res.data?.Data || res.data;

        const rawData = Array.isArray(apiData) ? apiData : [apiData];

        const allStatuses = [
          ...new Set(rawData.map((item) => item.status || "Unknown")),
        ];

        const buildPivot = (data, keyField, nameField) => {
          const map = {};

          data.forEach((item) => {
            const key = item[keyField] || "Unassigned";
            const name = item[nameField] || "Unassigned";
            const status = item.status || "Unknown";

            if (!map[key]) {
              map[key] = {
                name,
                counts: {},
                total: 0,
              };

              allStatuses.forEach((s) => {
                map[key].counts[s] = 0;
              });
            }

            map[key].counts[status]++;
            map[key].total++;
          });

          return Object.values(map);
        };

        dataToUse = {
          client: buildPivot(rawData, "ClientId", "ClientName"),
          project: buildPivot(rawData, "prTitle", "prTitle"),
          priority: buildPivot(rawData, "Priority", "Priority"),
          resource: buildPivot(rawData, "assignedTo", "assignedTo"),
          statuses: allStatuses,
        };
      }

      const wb = XLSX.utils.book_new();

      const createSheet = (sheetName, rows) => {
        if (!rows.length) return;

        const formatted = rows.map((row) => {
          let obj = { Name: row.name };

          dataToUse.statuses.forEach((s) => {
            obj[s] = row.counts[s];
          });

          obj["Total"] = row.total;

          return obj;
        });

        const ws = XLSX.utils.json_to_sheet(formatted);

        // ✅ Set column widths
        const colWidths = [
          { wch: 25 }, // Name column
          ...dataToUse.statuses.map(() => ({ wch: 15 })),
          { wch: 12 }, // Total
        ];

        ws["!cols"] = colWidths;

        XLSX.utils.book_append_sheet(wb, ws, sheetName);
      };

      // ✅ Create 4 sheets
      createSheet("Client", dataToUse.client);
      createSheet("Project", dataToUse.project);
      createSheet("Priority", dataToUse.priority);
      createSheet("Resource", dataToUse.resource);

      // ✅ Download file
      const excelBuffer = XLSX.write(wb, {
        bookType: "xlsx",
        type: "array",
      });

      const blob = new Blob([excelBuffer], {
        type: "application/octet-stream",
      });

      saveAs(blob, "Project_Progress_Report.xlsx");
    } catch (err) {
      console.error("Excel Download Error:", err);
    }
  };

  const handleApply = async () => {
    try {
      console.log("🔵 APPLY CLICKED");
      console.log("🔵 Filters BEFORE CLEAN:", filters);

      const cleanedFilters = Object.fromEntries(
        Object.entries({
          orgId: user?.organisationId,
          type: filters.type,
          client: filters.client,
          project: filters.project,
          resource: filters.resource,
          submittedBy: filters.submittedBy,
          dueStatus: filters.dueStatus,
          status: filters.status,
        }).filter(([_, value]) => value && value !== "All"),
      );

      console.log("🟡 CLEANED FILTERS:", cleanedFilters);

      const res = await axios.get(`${Base_URL}/ProjectProgress/list-v2`, {
        params: cleanedFilters,
      });
      // console.log("🟢 FULL API RESPONSE:", res);
      // console.log("🟢 RESPONSE DATA:", res.data);

      const apiData = res.data?.data || res.data?.Data || res.data;
      const rawData = Array.isArray(apiData) ? apiData : [];

      // console.log("🟣 RAW DATA LENGTH:", rawData.length);
      // console.log("🟣 SAMPLE ROW:", rawData[0]);

      if (!rawData.length) {
        // console.warn("⚠️ No data returned from API");
        setDashboardData({
          client: [],
          project: [],
          priority: [],
          resource: [],
          statuses: [],
        });
        return;
      }

      const allStatuses = [
        ...new Set(rawData.map((item) => item.status || "Unknown")),
      ];

      console.log("🟠 ALL STATUSES:", allStatuses);

      const buildPivot = (data, keyField, nameField) => {
        console.log(`🔷 Building pivot for ${keyField}`);

        const map = {};

        data.forEach((item, index) => {
          if (index < 5) {
            console.log("➡️ ITEM SAMPLE:", item);
          }

          const key = item[keyField] || "Unassigned";
          const name = item[nameField] || "Unassigned";
          const status = item.status || "Unknown";

          if (!map[key]) {
            map[key] = {
              name,
              counts: {},
              total: 0,
            };

            allStatuses.forEach((s) => {
              map[key].counts[s] = 0;
            });
          }

          map[key].counts[status]++;
          map[key].total++;
        });

        console.log(`✅ Pivot result for ${keyField}:`, Object.values(map));

        return Object.values(map);
      };

      const finalData = {
        client: buildPivot(rawData, "clientName", "clientName"),
        project: buildPivot(rawData, "prTitle", "prTitle"), // ✅ FIXED
        priority: buildPivot(rawData, "priority", "priority"),
        resource: buildPivot(rawData, "assignedToName", "assignedToName"),
        statuses: allStatuses,
      };

      console.log("🟢 FINAL DASHBOARD DATA:", finalData);

      setDashboardData(finalData);
    } catch (err) {
      console.error("❌ APPLY ERROR:", err);
    }
  };

  const handleClear = () => {
    const resetFilters = {
      type: "All",
      client: "",
      project: "",
      resource: "",
      submittedBy: "",
      fromDate: "",
      toDate: "",
      dueDate: "",
      status: "All",
      dueStatus: "All",
      changedFromDate: "",
      changedToDate: "",
    };

    setFilters(resetFilters);

    // ❌ REMOVE this (important)
    // setDashboardData({...})

    // ✅ Reload ALL data
    setTimeout(() => {
      handleApply();
    }, 0);
  };

  const toggleSection = (section) => {
    setExpanded((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  useEffect(() => {
    const fetchDropdowns = async () => {
      try {
        const res = await axios.get(
          `${Base_URL}/ProjectProgress/dropdowns-v2`,
          {
            params: { orgId: user?.organisationId },
          },
        );

        console.log("DROPDOWN API:", res.data);

        const data = res?.data?.data || res?.data?.Data || res?.data || {};
        setDropdownData({
          clients: data.clients || data.Clients || [],
          projects: data.projects || data.Projects || [],
          resources: data.resources || data.Resources || [],
          submittedBy: data.submittedBy || data.SubmittedBy || [],
          types: data.types || data.Types || [],
          dueStatus: data.dueStatuses || data.DueStatuses || [],
          statuses: data.statuses || data.Statuses || [],
        });
      } catch (err) {
        console.log("Dropdown error:", err);
      }
    };

    if (user?.organisationId) {
      fetchDropdowns();

      handleApply();
    }
  }, [user]);

  return (
    <div className="ppd-container">
      <div className="ppd-header">
        <h2 className="ppd-title">Project Progress Dashboard</h2>

        <button className="download-btn" onClick={handleDownloadExcel}>
          <span>⬇ Download Excel</span>
        </button>
      </div>

      <div className="ppd-filter-box">
        {/* Org ID */}
        <div className="ppd-field">
          <label>Organisation ID</label>
          <input value={user.organisationId || ""} disabled />
        </div>

        {/* Type */}
        <div className="ppd-field">
          <label>Type</label>
          <select
            value={filters.type}
            onChange={(e) => handleChange("type", e.target.value)}
          >
            <option value="All">All</option>

            {dropdownData.types.map((t, i) => (
              <option key={i} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>

        {/* Client */}
        <div className="ppd-field">
          <label>Client</label>
          <select
            value={filters.client}
            onChange={(e) => handleChange("client", e.target.value)}
          >
            <option value="">Select Client</option>

            {dropdownData.clients.map((c, i) => (
              <option key={i} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>

        {/* Project */}
        <div className="ppd-field">
          <label>Project</label>
          <select
            value={filters.project}
            onChange={(e) => handleChange("project", e.target.value)}
          >
            <option value="">Select Project</option>

            {dropdownData.projects.map((p, i) => (
              <option key={i} value={p}>
                {p}
              </option>
            ))}
          </select>
        </div>

        {/* Resource */}
        <div className="ppd-field">
          <label>Assigned To</label>
          <select
            value={filters.resource}
            onChange={(e) => handleChange("resource", e.target.value)}
          >
            <option value="">Select Resource</option>

            {dropdownData.resources.map((r, i) => (
              <option key={i} value={r}>
                {r}
              </option>
            ))}
          </select>
        </div>

        <div className="ppd-field">
          <label>Submitted By</label>
          <select
            value={filters.submittedBy}
            onChange={(e) => handleChange("submittedBy", e.target.value)}
          >
            <option value="">Select Submitted By</option>

            {dropdownData.submittedBy.map((s, i) => (
              <option key={i} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>

        {/* <div className="ppd-field">
          <label>Due Date</label>
          <input
            type="date"
            value={filters.dueDate}
            onChange={(e) => handleChange("dueDate", e.target.value)}
          />
        </div> */}

        <hr />

        <div className="ppd-field">
          <label>Due Status</label>
          <select
            value={filters.dueStatus || ""}
            onChange={(e) =>
              setFilters({ ...filters, dueStatus: e.target.value })
            }
          >
            <option value="">All</option>
            {dropdownData.dueStatus?.map((status, index) => (
              <option key={index} value={status}>
                {status}
              </option>
            ))}
          </select>
        </div>

        <div className="ppd-field">
          <label>Status</label>
          <select
            value={filters.status}
            onChange={(e) => handleChange("status", e.target.value)}
          >
            <option value="All">All</option>

            {dropdownData.statuses.map((s, i) => (
              <option key={i} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>

        {/* <div className="ppd-date-row">
          <div className="ppd-field">
            <label>Created From Date</label>
            <input
              type="date"
              value={filters.fromDate}
              onChange={(e) => handleChange("fromDate", e.target.value)}
            />
          </div>

          <div className="ppd-field">
            <label>Created To Date</label>
            <input
              type="date"
              value={filters.toDate}
              onChange={(e) => handleChange("toDate", e.target.value)}
            />
          </div>
        </div>

        {/* ✅ Changed Date Row */}
        {/* <div className="ppd-date-row">
          <div className="ppd-field">
            <label>Changed From Date</label>
            <input
              type="date"
              value={filters.changedFromDate}
              onChange={(e) => handleChange("changedFromDate", e.target.value)}
            />
          </div>

          <div className="ppd-field">
            <label>Changed To Date</label>
            <input
              type="date"
              value={filters.changedToDate}
              onChange={(e) => handleChange("changedToDate", e.target.value)}
            />
          </div>
        </div> */}
        <br />
        <br />

        <div className="ppd-buttons">
          <button className="apply-btn" onClick={handleApply}>
            Apply
          </button>
          <button className="clear-btn" onClick={handleClear}>
            Clear
          </button>
        </div>
      </div>

      <div className="ppd-list-sections">
        {/* CLIENT */}
        <div className="ppd-list-item">
          <div
            className="ppd-list-header"
            onClick={() => toggleSection("client")}
          >
            <span>Client → Status</span>
            <span>{expanded.client ? "▲" : "▼"}</span>
          </div>

          {expanded.client && (
            <div className="ppd-list-content">
              {dashboardData.client.length ? (
                <table className="ppd-table">
                  <thead>
                    <tr>
                      <th>Client</th>
                      {dashboardData.statuses.map((s, i) => (
                        <th key={i}>{s}</th>
                      ))}
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboardData.client.map((row, i) => (
                      <tr key={i}>
                        <td>{row.name}</td>

                        {dashboardData.statuses.map((s, j) => (
                          <td key={j}>{row.counts[s]}</td>
                        ))}

                        <td>{row.total}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="no-data">No Data</p>
              )}
            </div>
          )}
        </div>

        {/* PROJECT */}
        <div className="ppd-list-item">
          <div
            className="ppd-list-header"
            onClick={() => toggleSection("project")}
          >
            <span>Project → Status</span>
            <span>{expanded.project ? "▲" : "▼"}</span>
          </div>

          {expanded.project && (
            <div className="ppd-list-content">
              {dashboardData.project.length ? (
                <table className="ppd-table">
                  <thead>
                    <tr>
                      <th>Project</th>
                      {dashboardData.statuses.map((s, i) => (
                        <th key={i}>{s}</th>
                      ))}
                      <th>Total</th>
                    </tr>
                  </thead>

                  <tbody>
                    {dashboardData.project.map((row, i) => (
                      <tr key={i}>
                        <td>{row.name}</td>

                        {dashboardData.statuses.map((s, j) => (
                          <td key={j}>{row.counts[s]}</td>
                        ))}

                        <td>{row.total}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="no-data">No Data</p>
              )}
            </div>
          )}
        </div>

        {/* PRIORITY */}
        <div className="ppd-list-item">
          <div
            className="ppd-list-header"
            onClick={() => toggleSection("priority")}
          >
            <span>Priority → Status</span>
            <span>{expanded.priority ? "▲" : "▼"}</span>
          </div>

          {expanded.priority && (
            <div className="ppd-list-content">
              {dashboardData.priority.length ? (
                <table className="ppd-table">
                  <thead>
                    <tr>
                      <th>Priority</th>
                      {dashboardData.statuses.map((s, i) => (
                        <th key={i}>{s}</th>
                      ))}
                      <th>Total</th>
                    </tr>
                  </thead>

                  <tbody>
                    {dashboardData.priority.map((row, i) => (
                      <tr key={i}>
                        <td>{row.name}</td>

                        {dashboardData.statuses.map((s, j) => (
                          <td key={j}>{row.counts[s]}</td>
                        ))}

                        <td>{row.total}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="no-data">No Data</p>
              )}
            </div>
          )}
        </div>

        {/* RESOURCE */}
        <div className="ppd-list-item">
          <div
            className="ppd-list-header"
            onClick={() => toggleSection("resource")}
          >
            <span>Resource → Status</span>
            <span>{expanded.resource ? "▲" : "▼"}</span>
          </div>

          {expanded.resource && (
            <div className="ppd-list-content">
              {dashboardData.resource.length ? (
                <table className="ppd-table">
                  <thead>
                    <tr>
                      <th>Resource</th>
                      {dashboardData.statuses.map((s, i) => (
                        <th key={i}>{s}</th>
                      ))}
                      <th>Total</th>
                    </tr>
                  </thead>

                  <tbody>
                    {dashboardData.resource.map((row, i) => (
                      <tr key={i}>
                        <td>{row.name}</td>

                        {dashboardData.statuses.map((s, j) => (
                          <td key={j}>{row.counts[s]}</td>
                        ))}

                        <td>{row.total}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="no-data">No Data</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectProgressDashboard;
